import React from "react";
import type {ReactNode} from "react";
import {useAuth} from "../auth";
import {navigate, useRoute} from "../hooks";
import {Logo} from "./Logo";
import {NotificationBell, NotificationCriticalBanner, useAdminNotifications} from "./NotificationCenter";

const ADMIN_NAV_ITEMS = [
    {path: "#/admin/orders", label: "Orders"},
    {path: "#/admin/merchants", label: "Merchants"},
    {path: "#/admin/ip-allowlist", label: "IP Allowlist"},
    {path: "#/admin/traders", label: "Traders"},
    {path: "#/admin/disputes", label: "Disputes"},
    {path: "#/admin/telegram", label: "Telegram"},
    {path: "#/admin/history", label: "History"},
    {path: "#/admin/activity", label: "Activity"},
    {path: "#/admin/finance", label: "Finance"},
    {path: "#/admin/statistics", label: "Statistics"},
    {path: "#/admin/merchant-withdrawals", label: "Merchant withdrawals"},
] as const;

interface AdminLayoutProps {
    children: ReactNode;
}

/**
 * Каркас админ-панели: сайдбар с навигацией
 * и выходом. Повторяет стиль AppLayout трейдерского ЛК, но с другим
 * набором разделов и брэндом «Admin». Виден только админу — гейтинг
 * в App.tsx.
 */
export function AdminLayout({children}: AdminLayoutProps): JSX.Element {
    const {user, logout} = useAuth();
    const route = useRoute();
    const notifications = useAdminNotifications();

    return (
        <div className="app-layout admin-layout">
            <aside className="sidebar">
                <div className="sidebar-brand" onClick={() => navigate("#/admin")}>
                    <Logo size={24}/>
                    <span>Admin</span>
                </div>
                <nav className="sidebar-nav">
                    {ADMIN_NAV_ITEMS.map((item) => (
                        <a
                            key={item.path}
                            href={item.path}
                            className={`nav-item ${route.startsWith(item.path) ? "active" : ""}`}
                        >
                            {item.label}
                        </a>
                    ))}
                    <NotificationBell state={notifications}/>
                </nav>
                <div className="sidebar-footer">
                    <div className="user-info">
                        <div className="user-avatar">
                            {(user?.login || "A")[0].toUpperCase()}
                        </div>
                        <div className="user-meta">
                            <span className="user-name">{user?.login || "unknown"}</span>
                            <span className="user-role">{user?.roles?.join(", ") || "admin"}</span>
                        </div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={logout}>
                        Logout
                    </button>
                </div>
            </aside>
            <main className="main-content">
                <NotificationCriticalBanner state={notifications}/>
                {children}
            </main>
        </div>
    );
}
